import React, { useState } from 'react';

import { Modal } from 'antd';
import Icon from '@ant-design/icons';
import cx from 'classnames';

import { ReactComponent as CrossSVG } from '../../../../assets/sightx-icons/svg/icon-sx-cross.svg';
import { ReactComponent as CheckCircle } from '../../../../assets/sightx-icons/svg/sx-check-circle.svg';
import { ReactComponent as StarIcon } from '../../../../assets/sightx-icons/svg/sx-star-plus.svg';
import { UsersAndCadence } from './usersAndCadence';
import { UpgradeToProPayment } from './upgradeToProPayment';
import { Features, isFeatureEnabled } from '../../../../featureToggles/features';
import { IIssueProfessionalSubscriptionDTO } from '../../../../slices/upgradeAccount/upgradeToPro.thunk';
import { ConfirmPaymentSuccess } from './confirmPaymentSuccess';

import styles from './upgradeToProModal.module.scss';

interface Props {
  onClose: () => void;
}

enum UpgradeToProModalStatus {
  PAYMENT = 'PAYMENT',
  SUCCESS = 'SUCCESS',
  CONFIRMATION = 'CONFIRMATION',
  SUBSCRIPTION = 'SUBSCRIPTION'
}

const isTiersEnabled = isFeatureEnabled(Features.TrialsOfTiers);

export const UpgradeToProModal = (props: Props): JSX.Element => {
  const [status, setStatus] = useState<UpgradeToProModalStatus>(UpgradeToProModalStatus.SUBSCRIPTION);
  const [subscription, setSubscription] = useState<IIssueProfessionalSubscriptionDTO | null>(null);

  const changeToPayment = (subscription: IIssueProfessionalSubscriptionDTO) => {
    setStatus(UpgradeToProModalStatus.PAYMENT);
    setSubscription(subscription);
  };

  const changeToPaymentSuccess = () => {
    setSubscription(null);
    setStatus(UpgradeToProModalStatus.SUCCESS);
  };

  const changeToConfirmation = () => {
    setStatus(UpgradeToProModalStatus.CONFIRMATION);
  };

  const changeToSubscription = () => {
    setStatus(UpgradeToProModalStatus.SUBSCRIPTION);
    setSubscription(null);
  };

  const renderContent = () => {
    if ([UpgradeToProModalStatus.SUBSCRIPTION, UpgradeToProModalStatus.CONFIRMATION].includes(status)) {
      const isConfirmation = status === UpgradeToProModalStatus.CONFIRMATION;
      return <UsersAndCadence
        nextAction={changeToPayment}
        previousAction={isConfirmation ? changeToSubscription : props.onClose}
        isConfirmation={isConfirmation}
        onSubmit={changeToConfirmation}
      />;
    }

    if (status === UpgradeToProModalStatus.PAYMENT && subscription) {
      return <UpgradeToProPayment
        numberOfUsers={subscription.numberOfUsers}
        subscriptionCadence={subscription.subscriptionCadence}
        backAction={changeToSubscription}
        nextAction={changeToPaymentSuccess}
      />;
    }

    return <ConfirmPaymentSuccess />;
  };

  const renderTitle = () => {
    switch (status) {
    case UpgradeToProModalStatus.PAYMENT:
      return 'Payment method';
    case UpgradeToProModalStatus.SUCCESS:
      return 'Upgrading to Professional';
    case UpgradeToProModalStatus.CONFIRMATION:
      return isTiersEnabled ? 'Confirm account upgrade' : 'Upgrade confirmation';
    default:
      return isTiersEnabled ? 'Configure your subscription' : 'Upgrade to SightX Professional Plan';
    }
  };
  const featuresList = [
    'All-in-one research suite',
    'Advanced automated analytics',
    'Access to millions of global consumers',
    'Team collaboration'
  ];

  const info = <div className={cx(styles.info, {
    [styles[Features.PromotionalCodes]]: isFeatureEnabled(Features.PromotionalCodes)
  })}>
    <p>For deeper learning and more powerful insights across your organization.</p>
    <p>Everything in Lite, plus...</p>
    <ul>
      {featuresList.map((feature, index) => (
        <li key={index}>
          <Icon component={CheckCircle} />
          <span className={styles.list}>{feature}</span>
        </li>
      ))}
    </ul>
  </div>;

  if (isTiersEnabled) {
    const icon = <span className={styles.icon}>{isFeatureEnabled(Features.PromotionalCodes) ? <Icon component={StarIcon} /> : '✨'}</span>;
    return <Modal
      visible
      onCancel={props.onClose}
      className={cx(styles.tiersModal, {
        [styles[Features.PromotionalCodes]]: isFeatureEnabled(Features.PromotionalCodes)
      })}
      footer={null}
      width={820}
      maskClosable={status === UpgradeToProModalStatus.SUBSCRIPTION}
      keyboard={false}
      closeIcon={<Icon component={CrossSVG} />}
    >
      <div className={styles.header}>
        <span>Upgrade to <b>SightX Professional</b>{icon}</span>
      </div>
      <div className={styles.content}>
        <div>
          <div className={cx(styles.title, {
            [styles[Features.PromotionalCodes]]: isFeatureEnabled(Features.PromotionalCodes)
          })}>{renderTitle()}</div>
          {renderContent()}
        </div>
        {info}
      </div>
    </Modal>;
  }

  return (
    <Modal
      visible
      onCancel={props.onClose}
      className={styles.modal}
      footer={null}
      width={450}
      maskClosable={status === UpgradeToProModalStatus.SUBSCRIPTION}
      closable={false}
      keyboard={false}
    >
      <div className={styles.title}>{renderTitle()}</div>
      <div className={styles.content}>
        {renderContent()}
      </div>
    </Modal>
  );
};
